import noop from 'lodash/noop';
import { createTree, type TreeNode } from '../../../lib/array';
import { type TreeRow, type TreeRowParentOptions } from './types';

type RowHelper<Attributes = unknown> = Pick<TreeRow<Attributes>, 'parentId' | 'parentsIds' | 'level' | 'index'>;

function setNode<Attributes = unknown>(
    {
        rowNodes,
        map,
        treeNodes,
    }: {
        rowNodes: TreeRow[];
        map: Map<string, TreeRow<Attributes>>;
        treeNodes: TreeNode<Attributes>[];
    },
    rendersBefore: TreeRowParentOptions[] = [],
    sendIdToParent: (id: string) => void = noop,
    rowHelper: RowHelper<Attributes> = {
        parentId: undefined,
        parentsIds: new Set(),
        level: 0,
        index: 0,
    }
): number {
    let globalIndex = rowHelper.index;

    treeNodes.forEach(({ id, children, attributes }, i, arr) => {
        sendIdToParent(id);

        const currentRendersBefore: TreeRowParentOptions[] =
            rowHelper.parentId === undefined
                ? []
                : [
                      ...rendersBefore,
                      {
                          id: rowHelper.parentId,
                          vertical: i < arr.length - 1,
                      },
                  ];
        const childrenIds: Set<string> = new Set();

        const node: TreeRow<Attributes> = {
            ...rowHelper,
            childrenIds,
            id: id,
            attributes: attributes,
            rendersBefore: currentRendersBefore,
            hasChildren: Boolean(children?.length),
            isLastInParent: i >= arr.length - 1,
            index: globalIndex,
        };

        globalIndex += 1;

        rowNodes.push(node);
        map.set(id, node);

        if (children?.length) {
            globalIndex = setNode(
                {
                    rowNodes,
                    map,
                    treeNodes: children,
                },
                currentRendersBefore,
                (id) => {
                    childrenIds.add(id);
                    sendIdToParent(id);
                },
                {
                    parentId: id,
                    parentsIds: new Set([...rowHelper.parentsIds, id]),
                    level: rowHelper.level + 1,
                    index: globalIndex,
                }
            );
        }
    });

    return globalIndex;
}

export function flattenNodes<Attributes = unknown>(
    items: Attributes[],
    getIds: (att: Attributes) => {
        id: string;
        parentId: string | undefined;
    }
) {
    const treeNodes = createTree(items, getIds);
    const rows: TreeRow<Attributes>[] = [];
    const map: Map<string, TreeRow<Attributes>> = new Map();

    setNode({ rowNodes: rows, map, treeNodes });

    return { rows, map };
}
